
// client/src/pages/dashboard/DashboardPage.jsx

import React, { useEffect, useRef, useState } from "react";
import DashboardStatsCards    from "../../components/dashboard/DashboardStatsCards";
import DashboardFilters       from "../../components/dashboard/DashboardFilters";
import DashboardInvoicesTable from "../../components/dashboard/DashboardInvoicesTable";
import {
  getDashboardStats,
  getInvoices,
  getQuarters,
  exportReport,
} from "../../api/dashboardApi";

export default function DashboardPage() {

  // ── DATA STATE ──
  const [stats,    setStats]    = useState({});
  const [invoices, setInvoices] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters]   = useState({
    invoiceNumber: "",
    vendor: "",
    billingQuarter: "",
    paymentStatus: "",
  });

  // ── HEADER SCROLL ──
  const [hidden, setHidden]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (y > lastY.current && y > 120) setHidden(true);
      else setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── INITIAL LOAD ──
  useEffect(() => { loadInitialData(); }, []);

  // ── FILTER-DRIVEN INVOICE LOAD ──
  useEffect(() => {
    const hasFilter =
      filters.invoiceNumber ||
      filters.vendor ||
      filters.billingQuarter ||
      filters.paymentStatus;

    if (!hasFilter) { setInvoices([]); return; }

    const load = async () => {
      try {
        const data = await getInvoices(filters);
        setInvoices(data);
      } catch (err) { console.error(err); }
    };
    load();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [statsData, quarterData] = await Promise.all([
        getDashboardStats(),
        getQuarters(),
      ]);
      setStats(statsData);
      setQuarters(quarterData);
    } catch (err) { console.error(err); }
  };

  // ── EXPORT ──
  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await exportReport(filters);
      if (result?.downloadUrl) window.open(result.downloadUrl, "_blank");
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  // ── FILTER RESET ──
  const handleClearFilters = () => {
    setFilters({ invoiceNumber: "", vendor: "", billingQuarter: "", paymentStatus: "" });
  };

  const hasActiveFilter =
    filters.invoiceNumber ||
    filters.vendor ||
    filters.billingQuarter ||
    filters.paymentStatus;

  // ─────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────
  return (
    <div style={styles.page}>

      {/* ── HEADER ── */}
      <header style={{
        ...styles.header,
        ...(scrolled ? styles.headerScrolled : {}),
        ...(hidden   ? styles.headerHidden   : {}),
      }}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            <div style={styles.logoWrap}>📊</div>
            <div>
              <div style={styles.brandTitle}>Reports & Dashboard</div>
              <div style={styles.brandSubtitle}>Invoice Reporting Workspace</div>
            </div>
          </div>

         
        </div>
      </header>

      {/* ── MAIN ── */}
      <main style={styles.main}>

        {/* PAGE HEADER */}
        <div style={styles.pageHeader}>
          <div style={styles.eyebrowRow}>
            <span style={styles.eyebrowDot} />
            <span style={styles.eyebrow}>Finance</span>
            <span style={styles.eyebrowSep}>/</span>
            <span style={styles.eyebrowMuted}>Dashboard</span>
          </div>
          <div style={styles.pageTitleRow}>
            <div>
              <h1 style={styles.pageTitle}>Reports & Dashboard</h1>
              <p style={styles.pageSubtitle}>
                Monitor invoices, payments, vendor activity and billing performance across all quarters
              </p>
            </div>
            {/* Mobile-friendly secondary export */}
            <button
              style={{ ...styles.exportBtnAlt, ...(exporting ? styles.exportBtnBusy : {}) }}
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "↓ Export Report"}
            </button>
          </div>
        </div>

        {/* ── STATS CARDS (existing component) ── */}
        <div style={styles.section}>
          <DashboardStatsCards stats={stats} />
        </div>

        {/* ── FILTERS CARD ── */}
        <div style={styles.filterCard}>

          <div style={styles.filterCardHeader}>
            <div style={styles.filterCardLeft}>
              <div style={styles.filterIconWrap}>
                <span style={{ fontSize: "15px" }}>🔍</span>
              </div>
              <div>
                <div style={styles.filterTitle}>Search & Filter</div>
                <div style={styles.filterSubtitle}>
                  Narrow results by invoice number, vendor, quarter or payment status
                </div>
              </div>
            </div>

            {hasActiveFilter && (
              <button style={styles.clearBtn} onClick={handleClearFilters}>
                ✕ Clear Filters
              </button>
            )}
          </div>

          <div style={styles.filterDivider} />

          <DashboardFilters
            filters={filters}
            setFilters={setFilters}
            quarters={quarters}
          />
        </div>

        {/* ── RESULTS HEADER ROW ── */}
        <div style={styles.resultsHeaderRow}>
          <div style={styles.resultsLabel}>
            {hasActiveFilter
              ? <>Results <span style={styles.resultsBadge}>{invoices.length}</span></>
              : <span style={styles.resultsHint}>Apply a filter above to load invoice records</span>
            }
          </div>
        </div>

        {/* ── INVOICES TABLE (existing component) ── */}
        <div style={styles.tableCard}>
          <DashboardInvoicesTable invoices={invoices} />
        </div>

      </main>
    </div>
  );
}

// ─────────────────────────────────────
// STYLES
// ─────────────────────────────────────
const styles = {

  page: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // ── HEADER ──
  header: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    height: "56px",
    backgroundColor: "#0f172a",
    zIndex: 100,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    display: "flex",
    alignItems: "center",
  },
  headerScrolled: { boxShadow: "0 1px 0 rgba(255,255,255,0.06)" },
  headerHidden:   { transform: "translateY(-100%)" },
  headerInner: {
    width: "100%", maxWidth: "1400px",
    margin: "0 auto", padding: "0 40px",
    display: "flex", alignItems: "center",
    justifyContent: "space-between", boxSizing: "border-box",
  },
  brand:         { display: "flex", alignItems: "center", gap: "12px" },
  logoWrap: {
    fontSize: "20px", width: "36px", height: "36px",
    backgroundColor: "rgba(255,255,255,0.08)",
    borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  brandTitle:    { fontSize: "14px", fontWeight: "700", color: "#ffffff", letterSpacing: "-0.01em" },
  brandSubtitle: { fontSize: "11px", color: "rgba(255,255,255,0.45)", fontWeight: "400", marginTop: "1px" },

  headerRight: { display: "flex", alignItems: "center", gap: "12px" },

  refreshNote: { display: "flex", alignItems: "center", gap: "6px" },
  refreshDot:  { width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#10b981" },
  refreshText: { fontSize: "11px", color: "rgba(255,255,255,0.45)", fontWeight: "500" },

  exportBtn: {
    height: "30px", padding: "0 14px",
    fontSize: "11px", fontWeight: "700",
    color: "#ffffff", backgroundColor: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "6px", cursor: "pointer",
    transition: "background-color 0.15s",
    whiteSpace: "nowrap",
  },
  exportBtnBusy: { opacity: 0.6, cursor: "not-allowed" },

  statusPill: {
    display: "flex", alignItems: "center", gap: "7px",
    padding: "5px 12px",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "999px",
  },
  statusDot: {
    width: "7px", height: "7px", borderRadius: "50%",
    backgroundColor: "#10b981",
    boxShadow: "0 0 0 3px rgba(16,185,129,0.20)",
  },
  statusText: { fontSize: "11px", fontWeight: "600", color: "rgba(255,255,255,0.70)", letterSpacing: "0.02em" },

  // ── MAIN ──
  main: {
    paddingTop: "80px", paddingBottom: "60px",
    paddingLeft: "40px", paddingRight: "40px",
    maxWidth: "1400px", margin: "0 auto",
    boxSizing: "border-box", width: "100%",
  },

  pageHeader:    { marginBottom: "28px" },
  eyebrowRow:    { display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px" },
  eyebrowDot:    { width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,0.15)" },
  eyebrow:       { fontSize: "11px", fontWeight: "700", color: "#4f46e5", letterSpacing: "0.07em", textTransform: "uppercase" },
  eyebrowSep:    { fontSize: "11px", color: "#d1d5db" },
  eyebrowMuted:  { fontSize: "11px", fontWeight: "600", color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" },
  pageTitleRow:  { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" },
  pageTitle:     { fontSize: "18px", fontWeight: "700", color: "#111827", margin: 0, letterSpacing: "-0.01em" },
  pageSubtitle:  { fontSize: "13px", color: "#6b7280", margin: "4px 0 0 0", fontWeight: "400", maxWidth: "560px" },

  // Shown below page title on smaller viewports — hidden at wide widths via opacity trick
  exportBtnAlt: {
    height: "36px", padding: "0 16px",
    fontSize: "12px", fontWeight: "700",
    color: "#ffffff", backgroundColor: "#2563eb",
    border: "none", borderRadius: "7px",
    cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    alignSelf: "flex-start",
    transition: "background-color 0.15s",
  },

  section: { marginBottom: "24px" },

  // ── FILTER CARD ──
  filterCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    marginBottom: "20px",
    overflow: "hidden",
  },
  filterCardHeader: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
  },
  filterCardLeft:  { display: "flex", alignItems: "center", gap: "14px" },
  filterIconWrap: {
    width: "36px", height: "36px", borderRadius: "8px",
    backgroundColor: "#eff6ff",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  filterTitle:   { fontSize: "14px", fontWeight: "700", color: "#111827", letterSpacing: "-0.01em" },
  filterSubtitle:{ fontSize: "12px", color: "#9ca3af", marginTop: "2px", fontWeight: "400" },
  filterDivider: { height: "1px", backgroundColor: "#f3f4f6" },

  clearBtn: {
    height: "30px", padding: "0 12px",
    fontSize: "11px", fontWeight: "600",
    color: "#dc2626", backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px", cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background-color 0.15s",
  },

  // ── RESULTS HEADER ──
  resultsHeaderRow: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
    minHeight: "24px",
  },
  resultsLabel: {
    display: "flex", alignItems: "center", gap: "8px",
    fontSize: "11px", fontWeight: "700",
    color: "#374151", letterSpacing: "0.07em", textTransform: "uppercase",
  },
  resultsBadge: {
    fontSize: "11px", fontWeight: "700",
    color: "#4f46e5", backgroundColor: "#ede9fe",
    padding: "2px 8px", borderRadius: "999px",
  },
  resultsHint: {
    fontSize: "12px", fontWeight: "400",
    color: "#9ca3af", textTransform: "none", letterSpacing: 0,
  },

  // ── TABLE CARD ──
  tableCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    overflow: "hidden",
  },
};